import { useState, useEffect, useRef, useCallback } from 'react';

// Global registry to prevent browser garbage collection (especially in Chromium)
// from prematurely terminating active utterances mid-playback.
const globalActiveUtterances = new Set<SpeechSynthesisUtterance>();
if (typeof window !== 'undefined') {
  (window as any).__activeSpeechUtterances = globalActiveUtterances;
}

// Module-level timestamp to coordinate across all hook instances and prevent
// background instances from falsely detecting stuck state while another instance is speaking.
let globalLastSpeakTime = Date.now();

// Legacy novelty, robotic, or monotone synthesizer voices to avoid
const ROBOTIC_VOICE_KEYWORDS = [
  'albert', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos',
  'good news', 'jester', 'organ', 'superstar', 'trinoids', 'whisper',
  'wobble', 'zarvox', 'fred', 'ralph', 'junior', 'kathy', 'espeak', 'mbrola', 'klatt'
];

export function isRoboticVoice(voice: SpeechSynthesisVoice): boolean {
  const name = voice.name.toLowerCase();
  return ROBOTIC_VOICE_KEYWORDS.some(keyword => name.includes(keyword));
}

interface UseSpeechSynthesisProps {
  lang?: string; // e.g., 'pt-BR', 'en-US'
  rate?: number; // Voice speed, 1.0 is normal
}

/**
 * Splits text into smaller chunks at natural boundaries (sentences, clauses)
 * to work around Chrome macOS's ~15-second utterance timeout bug.
 * Each chunk will be under maxChars characters when possible.
 */
function splitTextIntoChunks(text: string, maxChars = 160): string[] {
  // Short text doesn't need splitting
  if (text.length <= maxChars) {
    return [text];
  }

  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }

    // Look for the best split point within maxChars
    const searchArea = remaining.substring(0, maxChars);

    // Priority 1: Split at sentence boundaries (. ! ?)
    let splitIndex = -1;
    const sentenceMatch = searchArea.match(/.*[.!?]/s);
    if (sentenceMatch) {
      splitIndex = sentenceMatch[0].length;
    }

    // Priority 2: Split at clause boundaries (, ; : — –)
    if (splitIndex === -1) {
      const clauseMatch = searchArea.match(/.*[,;:\u2014\u2013]/s);
      if (clauseMatch) {
        splitIndex = clauseMatch[0].length;
      }
    }

    // Priority 3: Split at the last space
    if (splitIndex === -1) {
      const lastSpace = searchArea.lastIndexOf(' ');
      if (lastSpace > 0) {
        splitIndex = lastSpace;
      }
    }

    // Fallback: just split at maxChars
    if (splitIndex === -1) {
      splitIndex = maxChars;
    }

    const chunk = remaining.substring(0, splitIndex).trim();
    if (chunk.length > 0) {
      chunks.push(chunk);
    }
    remaining = remaining.substring(splitIndex).trim();
  }

  return chunks.filter(c => c.length > 0);
}

/**
 * Selects the best available voice for a given language
 * Priority order:
 * 1. Modern AI / Neural / Natural Online voices (Edge Online Natural, Apple Siri, Personal Voice, Enhanced/Premium)
 * 2. High-quality Google voices (Google US English, Google UK English Female, etc.)
 * 3. High-quality studio/system voices (Samantha, Ava, Allison, Daniel, Serena, etc.)
 * 4. Default clean system voice for the language
 * 5. Any matching non-robotic voice
 */
export function selectBestVoice(voices: SpeechSynthesisVoice[], targetLang: string): SpeechSynthesisVoice | null {
  if (!voices || voices.length === 0) return null;

  const normalizedTarget = targetLang.toLowerCase().replace('_', '-');
  const langCode = normalizedTarget.split('-')[0]; // e.g., 'en' from 'en-US'
  const exactLocale = normalizedTarget; // e.g., 'en-us'

  // Filter voices that match the language
  const matchingVoices = voices.filter(v => {
    const voiceLang = v.lang.toLowerCase().replace('_', '-');
    return voiceLang === exactLocale || voiceLang.startsWith(langCode + '-') || voiceLang === langCode;
  });

  if (matchingVoices.length === 0) return null;

  // Filter out any robotic/novelty voices from matching candidates
  const cleanVoices = matchingVoices.filter(v => !isRoboticVoice(v));
  const fullPool = cleanVoices.length > 0 ? cleanVoices : matchingVoices;

  // Prioritize voices matching the exact regional accent (e.g. en-US vs en-GB) before falling back to other locales
  const exactLocaleVoices = fullPool.filter(v => {
    const voiceLang = v.lang.toLowerCase().replace('_', '-');
    const voiceName = v.name.toLowerCase();
    if (exactLocale === 'en-gb' || exactLocale === 'en-uk') {
      return (
        voiceLang === 'en-gb' ||
        voiceLang === 'en-uk' ||
        voiceName.includes('uk') ||
        voiceName.includes('british') ||
        voiceName.includes('united kingdom') ||
        voiceName.includes('great britain')
      );
    }
    if (exactLocale === 'en-us') {
      return (
        voiceLang === 'en-us' ||
        voiceName.includes('us english') ||
        voiceName.includes('united states')
      );
    }
    return voiceLang === exactLocale;
  });

  const pool = exactLocaleVoices.length > 0 ? exactLocaleVoices : fullPool;

  // Log available voices for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[SpeechSynthesis] Available clean voices for ${targetLang}:`,
      pool.map(v => `${v.name} (${v.lang})`).join(', '));
  }

  // 1. HIGHEST PRIORITY: Modern AI / Neural / Natural Online voices
  // (Microsoft Edge "Online (Natural)", Azure AI, ChromeOS Natural, Apple Siri, Personal Voice, Apple Enhanced/Premium)
  const aiKeywords = [
    'online (natural)',
    'natural',
    'neural',
    'neural2',
    'wavenet',
    'siri',
    'personal voice',
    'enhanced',
    'premium'
  ];
  const aiVoices = pool.filter(v =>
    aiKeywords.some(keyword => v.name.toLowerCase().includes(keyword))
  );

  if (aiVoices.length > 0) {
    // Prefer exact locale match (e.g. en-US over en-GB)
    const exactAi = aiVoices.find(v => v.lang.toLowerCase().replace('_', '-') === exactLocale);
    if (exactAi) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SpeechSynthesis] Selected AI/Neural voice: ${exactAi.name}`);
      }
      return exactAi;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`[SpeechSynthesis] Selected AI/Neural voice: ${aiVoices[0].name}`);
    }
    return aiVoices[0];
  }

  // 2. High-quality Google voices (best quality on standard Chrome)
  const googleVoices = pool.filter(v =>
    v.name.toLowerCase().includes('google')
  );

  if (googleVoices.length > 0) {
    if (exactLocale === 'en-gb' || exactLocale === 'en-uk') {
      const googleUK = googleVoices.find(v =>
        v.name.toLowerCase().includes('uk english female') ||
        v.name.toLowerCase().includes('uk english') ||
        v.lang.toLowerCase().replace('_', '-') === 'en-gb'
      );
      if (googleUK) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[SpeechSynthesis] Selected Google UK voice: ${googleUK.name}`);
        }
        return googleUK;
      }
    }

    if (exactLocale === 'en-us') {
      const googleUS = googleVoices.find(v =>
        v.name.toLowerCase().includes('us english') || v.lang.toLowerCase().replace('_', '-') === 'en-us'
      );
      if (googleUS) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[SpeechSynthesis] Selected Google US voice: ${googleUS.name}`);
        }
        return googleUS;
      }
    }

    // Prefer female Google voices (generally more natural for TTS)
    const googleFemale = googleVoices.find(v =>
      v.name.toLowerCase().includes('female')
    );
    if (googleFemale) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SpeechSynthesis] Selected Google female voice: ${googleFemale.name}`);
      }
      return googleFemale;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[SpeechSynthesis] Selected Google voice: ${googleVoices[0].name}`);
    }
    return googleVoices[0];
  }

  // 3. High-quality system voices by name (known good voices on macOS/Windows)
  const highQualityVoiceNames = [
    // macOS high-quality voices
    'samantha', 'ava', 'allison', 'daniel', 'karen', 'moira', 'tessa', 'fiona', 'veena',
    'alex', 'tom', 'oliver', 'kate', 'serena',
    // Windows / Edge high-quality voices
    'aria', 'jenny', 'guy', 'zira', 'david', 'mark', 'hazel', 'susan', 'george',
    // Other quality voices
    'joanna', 'salli', 'kendra', 'kimberly', 'ivy', 'matthew', 'justin', 'joey'
  ];

  const highQualityVoices = pool.filter(v =>
    highQualityVoiceNames.some(name => v.name.toLowerCase().includes(name))
  );

  if (highQualityVoices.length > 0) {
    const hqExact = highQualityVoices.find(v => v.lang.toLowerCase() === exactLocale);
    if (hqExact) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SpeechSynthesis] Selected high-quality voice: ${hqExact.name}`);
      }
      return hqExact;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`[SpeechSynthesis] Selected high-quality voice: ${highQualityVoices[0].name}`);
    }
    return highQualityVoices[0];
  }

  // 4. Default system voice for the language (from clean pool)
  const defaultVoices = pool.filter(v => v.default);
  if (defaultVoices.length > 0) {
    const defaultExact = defaultVoices.find(v => v.lang.toLowerCase() === exactLocale);
    if (defaultExact) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SpeechSynthesis] Selected default voice: ${defaultExact.name}`);
      }
      return defaultExact;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`[SpeechSynthesis] Selected default voice: ${defaultVoices[0].name}`);
    }
    return defaultVoices[0];
  }

  // 5. Exact locale match from clean pool
  const exactMatchVoice = pool.find(v => v.lang.toLowerCase() === exactLocale);
  if (exactMatchVoice) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[SpeechSynthesis] Selected exact locale voice: ${exactMatchVoice.name}`);
    }
    return exactMatchVoice;
  }

  // 6. Fallback: first voice in pool
  if (process.env.NODE_ENV === 'development') {
    console.log(`[SpeechSynthesis] Selected fallback voice: ${pool[0].name}`);
  }
  return pool[0];
}

// Detect Chrome on macOS - improved detection
function isChromeOnMacOS(): boolean {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || '';
  
  // Check for Chrome (not Edge, Opera, etc.)
  const isChrome = /chrome/.test(userAgent) && !/edge|edg|opr/.test(userAgent);
  
  // Check for macOS - multiple ways
  const isMacOS = /macintosh|mac os x|macintel/.test(userAgent) || 
                  /mac/.test(platform) ||
                  platform.includes('Mac');
  
  const result = isChrome && isMacOS;
  
  return result;
}

export function useSpeechSynthesis({ lang = 'en-US', rate = 0.85 }: UseSpeechSynthesisProps = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const voicesLoadedRef = useRef<boolean>(false);
  const isChromeMac = isChromeOnMacOS();
  const isSpeakingRef = useRef<boolean>(false);
  const lastSpeakTimeRef = useRef<number>(0);
  // Store timeouts/intervals for cleanup
  const activeTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const activeIntervalsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const checkStartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speakIdRef = useRef<number>(0); // Unique ID per speak attempt to prevent stale callbacks
  const onEndCallbackRef = useRef<(() => void) | null>(null);
  // Queue of text chunks remaining to be spoken (for chunked speech on Chrome macOS)
  const chunkQueueRef = useRef<string[]>([]);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      setIsSupported(true);

      // Handle Chrome extension message channel errors
      const handleChromeError = (event: ErrorEvent) => {
        if (event.message && event.message.includes('message channel closed')) {
          console.warn('[SpeechSynthesis] Chrome message channel error detected, resetting speech synthesis...');
          if (isChromeMac) {
            // Reset speech synthesis state
            setTimeout(() => {
              resetSpeechSynthesis();
            }, 100);
          }
        }
      };
      
      window.addEventListener('error', handleChromeError);
      window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && typeof event.reason === 'object' && 'message' in event.reason) {
          const message = String(event.reason.message);
          if (message.includes('message channel closed')) {
            console.warn('[SpeechSynthesis] Chrome message channel error (unhandled rejection), resetting speech synthesis...');
            if (isChromeMac) {
              setTimeout(() => {
                resetSpeechSynthesis();
              }, 100);
            }
          }
        }
      });

      const updateVoices = () => {
        try {
          const availableVoices = window.speechSynthesis.getVoices();
          if (availableVoices.length > 0) {
            voicesLoadedRef.current = true;
          }
          setVoices(availableVoices);
        } catch (error) {
          console.error('[SpeechSynthesis] Error getting voices:', error);
        }
      };

      // Some browsers load voices asynchronously
      const voicesChangedHandler = () => {
        updateVoices();
      };
      window.speechSynthesis.onvoiceschanged = voicesChangedHandler;
      
      // Initial load
      updateVoices();

      // Chrome on macOS often needs multiple attempts and longer delays
      const delays = isChromeMac ? [100, 300, 500, 1000] : [100];
      const timeouts = delays.map(delay => {
        const timeout = setTimeout(updateVoices, delay);
        activeTimeoutsRef.current.add(timeout);
        return timeout;
      });

      return () => {
        // Remove error handlers
        window.removeEventListener('error', handleChromeError);
        // Clean up all timeouts
        activeTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
        activeTimeoutsRef.current.clear();
        activeIntervalsRef.current.forEach(interval => clearInterval(interval));
        activeIntervalsRef.current.clear();
        // Clear any pending check timeouts
        if (checkStartTimeoutRef.current) {
          clearTimeout(checkStartTimeoutRef.current);
          checkStartTimeoutRef.current = null;
        }
        if (startCheckTimeoutRef.current) {
          clearTimeout(startCheckTimeoutRef.current);
          startCheckTimeoutRef.current = null;
        }
        // Clean up event handler
        window.speechSynthesis.onvoiceschanged = null;
        window.speechSynthesis.cancel();
        // Reset Chrome macOS stuck state
        if (isChromeMac) {
          // Force reset by canceling and clearing any pending state
          try {
            window.speechSynthesis.cancel();
            // Small delay to let cancel complete, then reset refs
            setTimeout(() => {
              isSpeakingRef.current = false;
              utteranceRef.current = null;
            }, 50);
          } catch (e) {
            console.warn('[SpeechSynthesis] Error during cleanup:', e);
          }
        }
      };
    } else {
      console.warn('[SpeechSynthesis] Speech synthesis is not supported in this browser');
    }
  }, [isChromeMac]);

  // Chrome macOS recovery: Reset speech synthesis if it gets stuck
  const resetSpeechSynthesis = useCallback(() => {
    if (!isSupported || !isChromeMac) return;

    const synth = window.speechSynthesis;
    try {
      // Cancel everything
      synth.cancel();
      globalActiveUtterances.clear();

      // Chrome bug fix: Always call resume() to unclog the queue
      // Chrome can get stuck in a paused state internally even if synth.paused is false
      synth.resume();

      // Invalidate any in-flight speak attempt
      speakIdRef.current++;
      // Clear chunk queue
      chunkQueueRef.current = [];
      // Reset state
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      utteranceRef.current = null;
      // Clear all timeouts/intervals
      activeTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      activeTimeoutsRef.current.clear();
      activeIntervalsRef.current.forEach(interval => clearInterval(interval));
      activeIntervalsRef.current.clear();
      if (checkStartTimeoutRef.current) {
        clearTimeout(checkStartTimeoutRef.current);
        checkStartTimeoutRef.current = null;
      }
      if (startCheckTimeoutRef.current) {
        clearTimeout(startCheckTimeoutRef.current);
        startCheckTimeoutRef.current = null;
      }

      console.log('[SpeechSynthesis] Chrome macOS: Speech synthesis reset (with resume)');
    } catch (error) {
      console.error('[SpeechSynthesis] Error resetting speech synthesis:', error);
    }
  }, [isSupported, isChromeMac]);

  // Speaks a single chunk of text (internal — use createAndSpeak for the public entry point)
  const speakSingleChunk = useCallback((text: string, targetLang: string, availableVoices: SpeechSynthesisVoice[], useVoiceSelection: boolean, overrideRate: number | undefined, currentSpeakId: number, isFirstChunk: boolean) => {
    try {
      const synth = window.speechSynthesis;

      // Helper to check if this speak attempt is still the active one
      const isStale = () => currentSpeakId !== speakIdRef.current;

      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;
      utterance.lang = targetLang;
      globalActiveUtterances.add(utterance);

      // Select the best available voice (skip on fallback attempt)
      if (useVoiceSelection) {
        const bestVoice = selectBestVoice(availableVoices, targetLang);
        if (bestVoice) {
          try {
            utterance.voice = bestVoice;
          } catch (error) {
            console.warn(`[SpeechSynthesis] Error setting voice, using default:`, error);
          }
        }
      }

      utterance.rate = overrideRate !== undefined ? overrideRate : rate;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      let onstartFired = false;

      utterance.onstart = () => {
        if (isStale()) return;
        if (startCheckTimeoutRef.current) {
          clearTimeout(startCheckTimeoutRef.current);
          activeTimeoutsRef.current.delete(startCheckTimeoutRef.current);
          startCheckTimeoutRef.current = null;
        }
        onstartFired = true;
        setIsSpeaking(true);
        isSpeakingRef.current = true;
      };

      utterance.onend = () => {
        globalActiveUtterances.delete(utterance);
        if (isStale()) return;

        // Check if there are more chunks to speak
        if (chunkQueueRef.current.length > 0) {
          const nextChunk = chunkQueueRef.current.shift()!;
          if (process.env.NODE_ENV === 'development') {
            console.log(`[SpeechSynthesis] Speaking next chunk (${chunkQueueRef.current.length} remaining):`, nextChunk.substring(0, 40));
          }
          // Small delay between chunks to let Chrome settle
          const chunkDelay = setTimeout(() => {
            activeTimeoutsRef.current.delete(chunkDelay);
            if (isStale()) return;
            speakSingleChunk(nextChunk, targetLang, availableVoices, useVoiceSelection, overrideRate, currentSpeakId, false);
          }, 50);
          activeTimeoutsRef.current.add(chunkDelay);
        } else {
          // All chunks done
          setIsSpeaking(false);
          isSpeakingRef.current = false;
          utteranceRef.current = null;
          lastSpeakTimeRef.current = Date.now(); // Reset the timer so stuck state check waits 15s AFTER speech ends
          globalLastSpeakTime = Date.now();
          if (onEndCallbackRef.current) {
            const cb = onEndCallbackRef.current;
            onEndCallbackRef.current = null;
            try {
              cb();
            } catch (err) {
              console.error('[SpeechSynthesis] Error in onEnd callback:', err);
            }
          }
        }
      };

      utterance.onerror = (e) => {
        globalActiveUtterances.delete(utterance);
        if (startCheckTimeoutRef.current) {
          clearTimeout(startCheckTimeoutRef.current);
          activeTimeoutsRef.current.delete(startCheckTimeoutRef.current);
          startCheckTimeoutRef.current = null;
        }

        if (e.error === 'interrupted' || e.error === 'canceled') {
          console.log(`[SpeechSynthesis] Speech ${e.error} (expected when canceling)`);
        } else {
          console.error(`[SpeechSynthesis] Error: ${e.error}`, {
            charIndex: e.charIndex,
            text: e.utterance.text,
            voice: e.utterance.voice?.name
          });
        }

        if (isStale()) return;
        // Clear remaining chunks on error
        chunkQueueRef.current = [];
        setIsSpeaking(false);
        isSpeakingRef.current = false;
        utteranceRef.current = null;
        lastSpeakTimeRef.current = Date.now();
        globalLastSpeakTime = Date.now();
        if (onEndCallbackRef.current) {
          const cb = onEndCallbackRef.current;
          onEndCallbackRef.current = null;
          try {
            cb();
          } catch (err) {
            console.error('[SpeechSynthesis] Error in onEnd callback on error:', err);
          }
        }
      };

      // Only cancel + resume before the first chunk (not between chunks)
      if (isFirstChunk) {
        synth.cancel();
      }

      const speakTimeout = setTimeout(() => {
        activeTimeoutsRef.current.delete(speakTimeout);
        if (isStale()) return;

        try {
          synth.resume();
          synth.speak(utterance);

          // Guard for Chrome on macOS: if onstart never fired, ensure state is synchronized without aborting active speech
          if (isChromeMac && isFirstChunk) {
            const fallbackTimeout = setTimeout(() => {
              activeTimeoutsRef.current.delete(fallbackTimeout);
              startCheckTimeoutRef.current = null;
              if (isStale()) return;

              if (!onstartFired) {
                if (synth.speaking || synth.pending) {
                  // Speech IS actually playing or queued but onstart didn't fire (Chrome + remote voice bug)
                  // Don't cancel — just synchronize state and ensure queue is unpaused.
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('[SpeechSynthesis] Chrome bug: onstart never fired but speech is active, updating state manually');
                  }
                  setIsSpeaking(true);
                  isSpeakingRef.current = true;
                  if (typeof synth.resume === 'function') {
                    synth.resume();
                  }
                } else {
                  // Speech is truly stuck (not speaking and not pending after delay), recover safely with best voice preserved
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('[SpeechSynthesis] Chrome bug: speech not active after delay, attempting safe recovery');
                  }
                  synth.cancel();
                  globalActiveUtterances.delete(utterance);
                  chunkQueueRef.current = [];
                  speakIdRef.current++;
                  const retryTimeout = setTimeout(() => {
                    activeTimeoutsRef.current.delete(retryTimeout);
                    // Safe retry while ALWAYS maintaining voice selection (avoid robotic fallbacks)
                    createAndSpeak(text, targetLang, availableVoices, true, overrideRate);
                  }, 150);
                  activeTimeoutsRef.current.add(retryTimeout);
                }
              }
            }, 800);
            activeTimeoutsRef.current.add(fallbackTimeout);
            startCheckTimeoutRef.current = fallbackTimeout;
          }
        } catch (error) {
          console.error('[SpeechSynthesis] Error calling speak():', error);
          if (!isStale()) {
            chunkQueueRef.current = [];
            setIsSpeaking(false);
            isSpeakingRef.current = false;
          }
        }
      }, isFirstChunk ? 50 : 10);
      activeTimeoutsRef.current.add(speakTimeout);
    } catch (error) {
      console.error("[SpeechSynthesis] Error creating speech utterance:", error);
      chunkQueueRef.current = [];
      setIsSpeaking(false);
    }
  }, [isChromeMac, rate]);

  const createAndSpeak = useCallback((text: string, targetLang: string, availableVoices: SpeechSynthesisVoice[], useVoiceSelection = true, overrideRate?: number) => {
    const currentSpeakId = speakIdRef.current;

    // On Chrome macOS, split long text into chunks to avoid the ~15s cutoff bug
    if (isChromeMac && text.length > 160) {
      const chunks = splitTextIntoChunks(text, 160);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SpeechSynthesis] Chrome macOS: Splitting text into ${chunks.length} chunks:`, chunks);
      }
      // Store remaining chunks (all except the first)
      chunkQueueRef.current = chunks.slice(1);
      // Speak the first chunk
      speakSingleChunk(chunks[0], targetLang, availableVoices, useVoiceSelection, overrideRate, currentSpeakId, true);
    } else {
      // Short text or non-Chrome: speak directly as a single utterance
      chunkQueueRef.current = [];
      speakSingleChunk(text, targetLang, availableVoices, useVoiceSelection, overrideRate, currentSpeakId, true);
    }
  }, [isChromeMac, speakSingleChunk]);

  const speak = useCallback((text: string, overrideLang?: string, overrideRate?: number, onEnd?: () => void) => {
    onEndCallbackRef.current = onEnd || null;
    const now = Date.now();
    const timeSinceLastSpeak = now - lastSpeakTimeRef.current;

    // Debounce rapid clicks (prevent multiple calls within 300ms)
    if (timeSinceLastSpeak < 300 && isSpeakingRef.current) {
      return;
    }

    lastSpeakTimeRef.current = now;
    globalLastSpeakTime = now;

    // Invalidate any previous speak attempt (their callbacks become stale)
    speakIdRef.current++;
    
    if (!isSupported) {
      console.error('[SpeechSynthesis] Speech synthesis is not supported in this browser');
      return;
    }

    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      console.warn('[SpeechSynthesis] Cannot speak empty text');
      return;
    }

    const targetLang = overrideLang || lang;
    const synth = window.speechSynthesis;

    // Cancel current speech immediately if actually speaking
    if (synth.speaking || synth.pending) {
      synth.cancel();
      isSpeakingRef.current = false;
      // Small delay to let cancel complete
      const cancelTimeout = setTimeout(() => {
        activeTimeoutsRef.current.delete(cancelTimeout);
        const availableVoices = synth.getVoices();
        if (availableVoices.length > 0) {
          createAndSpeak(text.trim(), targetLang, availableVoices, true, overrideRate);
        } else {
          createAndSpeak(text.trim(), targetLang, [], true, overrideRate);
        }
      }, 50);
      activeTimeoutsRef.current.add(cancelTimeout);
      return;
    }
    
    // Get voices immediately (synchronously) to keep user interaction chain
    let availableVoices = synth.getVoices();
    
    // If no voices, try to get them with a minimal delay
    if (availableVoices.length === 0) {
      console.warn('[SpeechSynthesis] No voices loaded yet, attempting to load...');
      
      // Try to trigger voice loading
      if (isChromeMac) {
        try {
          const dummyUtterance = new SpeechSynthesisUtterance('');
          dummyUtterance.volume = 0;
          synth.speak(dummyUtterance);
          synth.cancel();
        } catch (e) {
          console.warn('[SpeechSynthesis] Error triggering voice load:', e);
        }
      }
      
      // Try one more time immediately
      availableVoices = synth.getVoices();
    }
    
    // If still no voices, wait a bit but keep it short
    if (availableVoices.length === 0) {
      const maxAttempts = 5;
      let attempts = 0;
      
      const checkVoices = setInterval(() => {
        attempts++;
        availableVoices = synth.getVoices();
        
        if (availableVoices.length > 0) {
          clearInterval(checkVoices);
          activeIntervalsRef.current.delete(checkVoices);
          // Use minimal delay to keep user interaction chain
          createAndSpeak(text.trim(), targetLang, availableVoices, true, overrideRate);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkVoices);
          activeIntervalsRef.current.delete(checkVoices);
          console.warn('[SpeechSynthesis] Timeout waiting for voices, attempting to speak without voice selection');
          createAndSpeak(text.trim(), targetLang, [], true, overrideRate);
        }
      }, 50);
      activeIntervalsRef.current.add(checkVoices);
    } else {
      // Voices available, proceed immediately (synchronously if possible)
      createAndSpeak(text.trim(), targetLang, availableVoices, true, overrideRate);
    }
  }, [isSupported, lang, createAndSpeak, isChromeMac]);

  const cancel = useCallback(() => {
    if (isSupported) {
      // Invalidate any in-flight speak attempt
      speakIdRef.current++;
      onEndCallbackRef.current = null;
      // Clear chunk queue
      chunkQueueRef.current = [];
      globalActiveUtterances.clear();
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      utteranceRef.current = null;
      // Clear all timeouts/intervals
      activeTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      activeTimeoutsRef.current.clear();
      activeIntervalsRef.current.forEach(interval => clearInterval(interval));
      activeIntervalsRef.current.clear();
      if (checkStartTimeoutRef.current) {
        clearTimeout(checkStartTimeoutRef.current);
        checkStartTimeoutRef.current = null;
      }
      if (startCheckTimeoutRef.current) {
        clearTimeout(startCheckTimeoutRef.current);
        startCheckTimeoutRef.current = null;
      }
    }
  }, [isSupported]);

  // Keep-alive heartbeat to prevent Chromium from pausing or dropping speech mid-stream
  useEffect(() => {
    if (!isSpeaking || typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const heartbeat = setInterval(() => {
      const synth = window.speechSynthesis;
      if (synth.speaking && typeof synth.resume === 'function') {
        synth.resume();
      }
    }, 4000);

    return () => clearInterval(heartbeat);
  }, [isSpeaking]);

  // Monitor for stuck state in Chrome macOS
  useEffect(() => {
    if (!isSupported || !isChromeMac) return;
    
    const checkStuckState = setInterval(() => {
      const synth = window.speechSynthesis;
      
      // Only reset if:
      // 1. Chrome's synthesis engine claims it is speaking
      // 2. NO component in our app has an active registered utterance (globalActiveUtterances.size === 0)
      // 3. Local instance is not speaking or queueing
      // 4. At least 15 seconds have passed since the LAST speak request or speech end across the entire application
      if (
        synth.speaking &&
        globalActiveUtterances.size === 0 &&
        !isSpeakingRef.current &&
        !utteranceRef.current &&
        chunkQueueRef.current.length === 0
      ) {
        const timeSinceLastSpeak = Date.now() - globalLastSpeakTime;
        if (timeSinceLastSpeak > 15000) {
          console.warn('[SpeechSynthesis] Chrome macOS: Detected stuck state (stuck for >15s), resetting...');
          resetSpeechSynthesis();
        }
      }
    }, 5000); // Check every 5 seconds (less frequent to avoid interference)
    
    return () => {
      clearInterval(checkStuckState);
    };
  }, [isSupported, isChromeMac, resetSpeechSynthesis]);

  return {
    isSupported,
    isSpeaking,
    speak,
    cancel, // Maintain backward compatibility if this hook existed before
    stop: cancel, // Alias for clarity
    reset: resetSpeechSynthesis, // Chrome macOS recovery function
    voices
  };
}
